import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartFrame } from '../../../components/chart/chart-frame';

/**
 * Khung của một báo cáo **chưa đủ điều kiện chạy** — APM-REPORT-f.
 *
 * ## Vì sao vẽ khung rỗng thay vì ẩn hẳn
 *
 * Ẩn là cách hỏng im lặng: người dùng nhớ mang máng "có một biểu đồ velocity ở
 * đâu đó", không thấy nữa, rồi đi tìm trong cấu hình — hoặc kết luận là mất
 * tính năng. Khung rỗng nói ra cả hai điều họ cần: **nó tồn tại**, và **thiếu
 * gì thì nó chạy**.
 *
 * ## ⚠️ Câu chữ phải là ĐIỀU KIỆN, không phải "chưa có dữ liệu"
 *
 * Đây là chỗ bản trước sai: biểu đồ Velocity ở dự án Waterfall hiện "chưa có
 * sprint nào" — đúng, nhưng nó đọc như một lời mời đi tạo sprint, mà Waterfall
 * thì không có sprint và sẽ không bao giờ có.
 *
 * Nên mỗi chỗ dùng phải nói **việc cần làm** (“chốt kế hoạch gốc ở tab Công
 * việc”) hoặc **vì sao không áp dụng** (“dự án Waterfall không chạy sprint”).
 * Một câu mô tả trạng thái mà không nói bước tiếp theo là một câu bắt người đọc
 * tự đoán.
 */
export function ReportPending({
  title,
  help,
  reason,
}: {
  readonly title: string;
  /**
   * Khoá `chartHelp.<key>` — **bắt buộc**.
   *
   * Nút (i) phải hoạt động **kể cả khi báo cáo chưa chạy**: đó chính là lúc
   * người dùng cần đọc "biểu đồ này nói gì" nhất, vì họ đang quyết định có
   * đáng đi thoả mãn điều kiện hay không. Repo có một test quét mọi component
   * biểu đồ và bắt thiếu khoá này (`chart-help.spec.tsx`).
   */
  readonly help: string;
  /** Câu nói **cần gì** để báo cáo chạy, hoặc vì sao nó không áp dụng. */
  readonly reason: string;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <ChartFrame
      title={title}
      help={help}
      loading={false}
      error={null}
      empty={{ title: t('reports.pendingTitle'), description: reason }}
    >
      {/* `empty` đã chiếm chỗ; children không bao giờ được vẽ. */}
      {null}
    </ChartFrame>
  );
}
